import { memo } from "react";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";

import { getStatusUi, templateStatusChipSx } from "../utils/messageTemplateUiUtils";

export const MessageTemplatesTableTemplateCell = memo(
  ({ displayName }: { displayName: string }) => (
    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ py: 0.25 }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          backgroundColor: "rgba(37, 211, 102, 0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          mt: 0.15,
        }}
      >
        <WhatsAppIcon sx={{ color: "#25D366", fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={600} noWrap title={displayName}>
          {displayName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Modelo WhatsApp
        </Typography>
      </Box>
    </Stack>
  ),
);

MessageTemplatesTableTemplateCell.displayName = "MessageTemplatesTableTemplateCell";

export const MessageTemplatesTableStatusCell = memo(({ status }: { status?: string }) => {
  const statusUi = getStatusUi(status);
  return (
    <Chip
      label={statusUi.label}
      color={statusUi.color}
      size="small"
      variant="outlined"
      sx={templateStatusChipSx}
    />
  );
});

MessageTemplatesTableStatusCell.displayName = "MessageTemplatesTableStatusCell";

export const MessageTemplatesTablePreviewCell = memo(({ previewPlain }: { previewPlain: string }) => {
  if (!previewPlain) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Tooltip
      title={
        <Typography
          component="span"
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", display: "block", maxWidth: 360 }}
        >
          {previewPlain}
        </Typography>
      }
      placement="top"
      enterDelay={400}
    >
      <Typography
        variant="body2"
        sx={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          lineHeight: 1.45,
          fontSize: "0.8125rem",
          color: "text.primary",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          cursor: "default",
          maxWidth: 360,
        }}
      >
        {previewPlain}
      </Typography>
    </Tooltip>
  );
});

MessageTemplatesTablePreviewCell.displayName = "MessageTemplatesTablePreviewCell";

export const MessageTemplatesTableCreatedCell = memo(({ createdAtIso }: { createdAtIso: string }) => {
  const created = dayjs(createdAtIso);

  if (!created.isValid()) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
        {created.format("DD/MM/YYYY")}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {created.format("HH:mm")}
      </Typography>
    </Stack>
  );
});

MessageTemplatesTableCreatedCell.displayName = "MessageTemplatesTableCreatedCell";
